/**
 * Gamification persistence: quest completions, lore purchases, activity log.
 * Writes go through /api/gamification (service-role); reads stay direct on
 * Supabase (RLS SELECT — user reads their own rows).
 */
import { supabase } from '../lib/supabase';
import { apiFetch } from './api-client';

export interface QuestCompletionRow {
  id: string;
  customer_address: string;
  quest_id: number;
  reward_beans: number;
  completed_at: string;
}

export interface LorePurchaseRow {
  id: string;
  customer_address: string;
  lore_id: string;
  cost_beans: number;
  purchased_at: string;
}

export interface ActivityLogRow {
  id: string;
  user_address: string;
  event_type: string;
  message: string;
  color: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

// ─── Writes ────────────────────────────────────────────

export async function completeQuest(questId: number, rewardBeans: number): Promise<QuestCompletionRow | null> {
  try {
    return await apiFetch<QuestCompletionRow>('/api/gamification', {
      action: 'quest-complete', questId, rewardBeans,
    });
  } catch (err: any) {
    console.error('completeQuest failed:', err?.message || err);
    return null;
  }
}

export async function purchaseLore(loreId: string, costBeans: number): Promise<LorePurchaseRow | null> {
  try {
    return await apiFetch<LorePurchaseRow>('/api/gamification', {
      action: 'lore-purchase', loreId, costBeans,
    });
  } catch (err: any) {
    console.error('purchaseLore failed:', err?.message || err);
    return null;
  }
}

export async function logActivity(params: {
  eventType: string;
  message: string;
  color?: string;
  metadata?: Record<string, unknown>;
}): Promise<void> {
  try {
    await apiFetch('/api/gamification', {
      action: 'activity-log',
      eventType: params.eventType,
      message: params.message,
      color: params.color,
      metadata: params.metadata,
    });
  } catch (err: any) {
    // Activity log is best-effort — don't disrupt UI on failure.
    console.warn('logActivity failed (non-fatal):', err?.message || err);
  }
}

// ─── Reads (direct Supabase, RLS-gated) ─────────────────

export async function getQuestCompletions(customerAddress: string): Promise<QuestCompletionRow[]> {
  const { data, error } = await supabase
    .from('quest_completions')
    .select('*')
    .eq('customer_address', customerAddress)
    .order('completed_at', { ascending: false });
  if (error || !data) return [];
  return data;
}

export async function getLorePurchases(customerAddress: string): Promise<LorePurchaseRow[]> {
  const { data, error } = await supabase
    .from('lore_purchases')
    .select('*')
    .eq('customer_address', customerAddress)
    .order('purchased_at', { ascending: false });
  if (error || !data) return [];
  return data;
}

export async function getActivityLog(customerAddress: string, limit = 50): Promise<ActivityLogRow[]> {
  const { data, error } = await supabase
    .from('activity_log')
    .select('*')
    .eq('user_address', customerAddress)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error || !data) return [];
  return data;
}

/** Streak saver (041): server validates + charges; returns repaired week or null. */
export async function repairStreak(shopId: string): Promise<{ week: string; cost: number } | null> {
  try {
    return await apiFetch<{ week: string; cost: number }>('/api/gamification', {
      action: 'streak-repair', shopId,
    });
  } catch (err: any) {
    console.error('repairStreak failed:', err?.message || err);
    return null;
  }
}

/** Shout-a-mate (041): consume the stashed ?ref= link after login. */
export async function attributeReferral(referrer: string): Promise<void> {
  try {
    await apiFetch('/api/account', { action: 'referral-attribute', referrer });
  } catch (err: any) {
    console.error('attributeReferral failed:', err?.message || err);
  }
}
